import { useState } from 'react';
import {
  collection, doc, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../hooks/useSettings';
import { Button, Modal, ModalBanner, FormField, EntityCombobox } from './ui';
import { fmtMoney } from '../utils/currency';
import {
  buildEntry, postEntry, entryId, linesSupplierAdvance, linesCustomerAdvance,
} from '../utils/ledger';
import type { Entity, BankAccount } from '../types';

function todayISO() { return new Date().toISOString().slice(0, 10); }
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

export default function RecordAdvanceModal({ kind, entities, accounts, onClose }: {
  kind: 'supplier' | 'customer';
  entities: Entity[];
  accounts: BankAccount[];
  onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const isSup = kind === 'supplier';

  const [entityId, setEntityId] = useState('');
  const [accountId, setAccountId] = useState((accounts.find(a => a.isDefault) ?? accounts[0])?.id ?? '');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const acct = accounts.find(a => a.id === accountId) ?? null;
  const picks = entities.filter(e => e.types?.includes(isSup ? 'supplier' : 'customer'));

  async function save() {
    const amt = parseFloat(amount);
    if (!entityId) return setError(`Choose the ${isSup ? 'supplier' : 'customer'}.`);
    if (!accountId) return setError(isSup ? 'Choose the account you paid from.' : 'Choose the account it came into.');
    if (isNaN(amt) || amt <= 0) return setError('Enter a valid amount.');
    if (!date) return setError('Pick the date.');
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business || !acct) { setError('Not connected.'); setSaving(false); return; }

    const coll = isSup ? 'supplierAdvances' : 'customerAdvances';
    const prefix = ((isSup ? settings.supplierAdvancePrefix : settings.customerAdvancePrefix) || (isSup ? 'SA' : 'CA')).toUpperCase();
    const advRef = doc(collection(db, coll));
    const txRef  = doc(collection(db, 'bankTransactions'));
    const accRef = doc(db, 'bankAccounts', acct.id);
    const counterRef = doc(db, 'counters', business.id);

    try {
      await runTransaction(db, async tx => {
        const cs = await tx.get(counterRef);
        const accSnap = await tx.get(accRef);
        if (!accSnap.exists()) throw new Error('That bank account no longer exists.');
        const n = (cs.exists() ? (cs.data()[isSup ? 'supplierAdvance' : 'customerAdvance'] ?? 0) : 0) + 1;

        tx.set(counterRef, { [isSup ? 'supplierAdvance' : 'customerAdvance']: n }, { merge: true });
        tx.set(advRef, stripUndef({
          businessId: business.id,
          advanceNo: `${prefix}-${new Date().getFullYear()}-${String(n).padStart(4, '0')}`,
          ...(isSup ? { fromEntityId: entityId } : { toEntityId: entityId }),
          date, amountUSD: amt, currency: acct.currency,
          bankAccountId: acct.id, bankAccountName: acct.name, bankTransactionId: txRef.id,
          appliedUSD: 0, status: 'open',
          notes: notes.trim() || undefined,
          createdAt: serverTimestamp(), createdBy: user!.id,
        }));

        tx.set(txRef, stripUndef({
          businessId: business.id, accountId: acct.id, accountName: acct.name,
          currency: acct.currency, direction: isSup ? 'out' : 'in', amount: amt, date,
          description: `${isSup ? 'Advance to' : 'Advance from'} ${picks.find(p => p.id === entityId)?.companyName ?? ''}`.trim(),
          referenceType: isSup ? 'supplier_advance' : 'customer_advance', referenceId: advRef.id,
          notes: notes.trim() || undefined,
          createdAt: new Date().toISOString(), createdBy: user!.id,
        }));
        const bal = (accSnap.data() as BankAccount).currentBalance;
        tx.update(accRef, { currentBalance: isSup ? bal - amt : bal + amt });

        postEntry(tx, db, buildEntry({
          businessId: business.id,
          sourceType: isSup ? 'supplier_advance' : 'customer_advance',
          sourceId: advRef.id, sourceCollection: coll,
          date, currency: acct.currency, createdBy: user!.id,
          note: `Advance ${isSup ? 'to supplier' : 'from customer'}`,
          lines: isSup
            ? linesSupplierAdvance(amt, entityId, acct.id)
            : linesCustomerAdvance(amt, entityId, acct.id),
        }), entryId(isSup ? 'supplier_advance' : 'customer_advance', advRef.id));
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the advance.');
    }
    setSaving(false);
  }

  const pickedName = picks.find(p => p.id === entityId)?.companyName;

  return (
    <Modal open onClose={onClose} hideHead
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>Record</Button>
      </>}>
      <ModalBanner
        title={pickedName || (isSup ? 'Record Supplier Advance' : 'Record Customer Advance')}
        eyebrow={isSup ? 'Supplier Advance' : 'Customer Advance'}
        onClose={onClose}
      />

      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
      <div className="form-row-2">
        <FormField label={isSup ? 'Supplier' : 'Customer'} required>
          <EntityCombobox
            entities={picks.map(e => ({ id: e.id, companyName: e.companyName }))}
            value={entityId} onChange={setEntityId}
          />
        </FormField>
        <FormField label="Date" required>
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
        </FormField>
      </div>
      <div className="form-row-2">
        <FormField label={isSup ? 'Paid from' : 'Received into'} required>
          <select value={accountId} onChange={e => setAccountId(e.target.value)}>
            <option value="">Select…</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name} — {fmtMoney(a.currentBalance, a.currency)}</option>)}
          </select>
        </FormField>
        <FormField label={`Amount (${acct?.currency ?? settings.currency})`} required>
          <input type="number" step="0.01" min="0.01" value={amount} className="no-spinner"
            onChange={e => setAmount(e.target.value)} />
        </FormField>
      </div>
      <FormField label="Notes (optional)">
        <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Reference, PO number…" />
      </FormField>
    </Modal>
  );
}
